import { Heading, HStack, PageContainer, VStack } from '@/components/Atoms'
import { useCrtVersionSelection } from '@/hooks/crt/useCrtVersionSelection'
import { useMe } from '@/hooks/queries/useMe'
import { parseDateOnly } from '@/utils/DateOnlyHelpers'
import { PRODUCT_CATEGORY, type ProductCategory } from '@repo/constants'
import { getUserGivenName } from '@repo/utils'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AssumptionsBreakdownCard } from './-components/assumptions-breakdown/AssumptionsBreakdownCard'
import { CptoGrossMarginWalkCard } from './-components/cpto-gross-margin-walk/CptoGrossMarginWalkCard'
import { CrtInsightsRefresh } from './-components/CrtInsightsRefresh'
import { InsightsCategorySelect } from './-components/InsightsCategorySelect'
import { FmcVolumeDistributionCard } from './-components/fmc-volume-distribution/FmcVolumeDistributionCard'
import { LmcTargetsCard } from './-components/lmc-targets/LmcTargetsCard'
import { MultiSopOverviewCard } from './-components/multi-sop-overview/MultiSopOverviewCard'
import { PerChannelOverviewCard } from './-components/per-channel-overview/PerChannelOverviewCard'
import { StakeholderSummaryCard } from './-components/stakeholder-summary/StakeholderSummaryCard'

export const Route = createFileRoute('/_auth/_modules/crt/insights/')({
  component: RouteComponent,
})

/**
 * The Insights-card query-key roots that carry the CRT period, matched when cancelling the outgoing
 * period's in-flight fetches. The flat roots hold the period at index 1; the `varianceAnalysis*` keys
 * nest it inside their comparison/side objects, so they are matched by prefix and by searching the key
 * for the outgoing period below. Multi-sop is keyed by year, not period, so a same-year month change
 * never refetches it — it is deliberately absent.
 */
const INSIGHTS_PERIOD_QUERY_ROOTS = new Set([
  'pnlByCategory',
  'assumptionsWalk',
  'perChannelOverview',
  'perChannelBreakdown',
])

/**
 * Cancels the in-flight Insights-card queries for the selection being navigated away from — another period,
 * or another version within the same one. Cancelling a query aborts its HTTP request, so the backend's
 * `c.req.raw.signal` fires and its live-summary limiter drops the queued legs before they allocate — this
 * is what stops thrashing the header selector from stacking computes nobody is viewing.
 *
 * Matched to the *outgoing* period and to queries no card observes any more. The cards re-subscribe to the
 * incoming selection's keys in their own effects, which run before this page's, so by now the outgoing keys
 * have lost their observers while the incoming ones (even those sharing the period) hold theirs and are never
 * touched. Queries are only cancelled, not removed, so revisiting a selection still serves from cache
 * (#802's `gcTime`/persister are untouched).
 */
function cancelInsightsQueries(queryClient: QueryClient, outgoingPeriod: string) {
  void queryClient.cancelQueries({
    predicate: (query) => {
      const root = query.queryKey[0]
      if (typeof root !== 'string' || query.getObserversCount() > 0) return false
      const isInsightsRoot =
        INSIGHTS_PERIOD_QUERY_ROOTS.has(root) || root.startsWith('varianceAnalysis')
      return isInsightsRoot && JSON.stringify(query.queryKey).includes(`"${outgoingPeriod}"`)
    },
  })
}

function RouteComponent() {
  const { data: me } = useMe()
  const { t } = useTranslation('crt')
  const queryClient = useQueryClient()

  const { selection } = useCrtVersionSelection()
  const periodDate = parseDateOnly(selection.crtPeriod)

  // The product category drives the whole page: every card reads it, and `undefined` ("All") totals every
  // category. A card that only speaks for one category is hidden when another is selected.
  const [productCategory, setProductCategory] = useState<ProductCategory | undefined>()
  const isFmcRelevant = productCategory === undefined || productCategory === PRODUCT_CATEGORY.FMC

  // The period and version move from the global header selector, and every card follows both. When either
  // changes, cancel the outgoing selection's in-flight Insights fetches so their backend legs are dropped
  // rather than left computing for a selection we're leaving (the OOM/thrash fix from #806).
  const previousSelectionRef = useRef(selection)
  useEffect(() => {
    const outgoing = previousSelectionRef.current
    if (outgoing.crtPeriod !== selection.crtPeriod || outgoing.versionId !== selection.versionId) {
      cancelInsightsQueries(queryClient, outgoing.crtPeriod)
      previousSelectionRef.current = selection
    }
  }, [selection, queryClient])

  const messageForTimeOfDay = (() => {
    const hour = new Date().getHours()
    if (hour < 12) return 'crt.greetings.morning'
    if (hour < 18) return 'crt.greetings.afternoon'
    return 'crt.greetings.evening'
  })()

  const greetingMessage = me
    ? t(messageForTimeOfDay, { name: getUserGivenName(me) })
    : t('crt.greetings.welcome')

  return (
    <PageContainer maxWidth="wide">
      <VStack gap="lg" className="py-6">
        <VStack gap="md">
          <Heading level={1} className="text-5xl font-medium">
            {greetingMessage}
          </Heading>
          <Heading level={4} tone="muted" className="max-w-[700px] text-balance font-normal">
            {t('crt.greetings.description')}
          </Heading>
        </VStack>
        <HStack className="w-full flex-wrap justify-between gap-4">
          <InsightsCategorySelect value={productCategory} onChange={setProductCategory} />
          <CrtInsightsRefresh />
        </HStack>
        <StakeholderSummaryCard periodDate={periodDate} productCategory={productCategory} />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <AssumptionsBreakdownCard periodDate={periodDate} productCategory={productCategory} />
          <CptoGrossMarginWalkCard periodDate={periodDate} productCategory={productCategory} />
        </div>
        {isFmcRelevant && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <FmcVolumeDistributionCard periodDate={periodDate} />
          </div>
        )}
        <PerChannelOverviewCard periodDate={periodDate} productCategory={productCategory} />
        <MultiSopOverviewCard periodDate={periodDate} productCategory={productCategory} />
        <LmcTargetsCard periodDate={periodDate} productCategory={productCategory} />
      </VStack>
    </PageContainer>
  )
}
